import { useState, useRef } from "react";
import { Upload, X, Image, FileText, ZoomIn } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DiagramUploadRenderer — File upload for diagrams/images
 * 
 * Expected item shape:
 * {
 *   type: "diagram_upload",
 *   id: "du_1",
 *   prompt: "Upload your architecture diagram",
 *   accept: "image/*" (optional, defaults to images + pdf),
 *   max_size_mb: 10 (optional)
 * }
 */
export function DiagramUploadRenderer({ item, index, responses, onResponseChange }) {
  const id = item.id || `diagram_upload_${index}`;
  const prompt = item.prompt || "Upload your diagram or image";
  const accept = item.accept || "image/png,image/jpeg,image/webp,application/pdf";
  const maxSizeMB = item.max_size_mb || 10;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  // The response stores: { asset_id, url, filename, content_type, size_bytes }
  const uploadData = responses[id] ? (typeof responses[id] === 'string' ? JSON.parse(responses[id]) : responses[id]) : null;

  const handleFile = async (file) => {
    if (!file) return;
    setError("");

    // Size check
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File too large. Maximum ${maxSizeMB}MB.`);
      return;
    }

    // Type check
    const allowedTypes = accept.split(",").map(t => t.trim());
    const typeMatch = allowedTypes.some(t => {
      if (t.endsWith("/*")) return file.type.startsWith(t.replace("/*", ""));
      return file.type === t;
    });
    if (!typeMatch) {
      setError(`Unsupported file type: ${file.type}`);
      return;
    }

    // Create local preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    // Upload to assessment assets endpoint
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", `${prompt} - ${file.name}`);

      const res = await fetch(`${API}/assessment-assets/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Upload failed (${res.status})`);
      }

      const data = await res.json();
      const uploadResult = JSON.stringify({
        asset_id: data.asset_id,
        url: data.url,
        filename: file.name,
        content_type: data.content_type,
        size_bytes: data.size_bytes,
      });
      onResponseChange(id, uploadResult);
    } catch (err) {
      setError(err.message || "Upload failed");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    onResponseChange(id, "");
    setPreview(null);
    setError("");
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // If file is already uploaded, show preview
  if (uploadData && uploadData.asset_id) {
    const isImage = uploadData.content_type?.startsWith("image/");
    const displayUrl = uploadData.url?.startsWith("/") ? `${API.replace("/api", "")}${uploadData.url}` : uploadData.url;

    return (
      <div key={index} className="my-4" data-testid={`diagram-upload-${index}`}>
        <p className="text-sm font-medium text-[#3b3b3b] mb-2">{prompt}</p>
        <div className="border-2 border-[#00AEEF]/30 rounded-lg bg-[#00AEEF]/5 p-4">
          <div className="flex items-start gap-4">
            {isImage ? (
              <div className="relative group">
                <img
                  src={displayUrl}
                  alt={uploadData.filename}
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                  data-testid={`diagram-preview-${index}`}
                />
                <a
                  href={displayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg flex items-center justify-center"
                >
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            ) : (
              <div className="w-32 h-32 rounded-lg border border-gray-200 bg-white flex items-center justify-center">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-[#3b3b3b]">{uploadData.filename}</p>
              <p className="text-xs text-gray-500 mt-1">
                {uploadData.content_type} &middot; {formatSize(uploadData.size_bytes)}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemove}
                className="mt-3 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                data-testid={`diagram-remove-${index}`}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Upload area
  return (
    <div key={index} className="my-4" data-testid={`diagram-upload-${index}`}>
      <p className="text-sm font-medium text-[#3b3b3b] mb-2">{prompt}</p>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
          dragOver
            ? "border-[#00AEEF] bg-[#00AEEF]/10"
            : "border-gray-300 hover:border-[#00AEEF]/50 bg-gray-50 hover:bg-[#00AEEF]/5"
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid={`diagram-dropzone-${index}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid={`diagram-file-input-${index}`}
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 border-3 border-[#00AEEF] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : preview ? (
          <div className="flex flex-col items-center gap-2">
            <img src={preview} alt="Preview" className="w-24 h-24 object-cover rounded" />
            <p className="text-sm text-gray-600">Processing...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#00AEEF]/10 flex items-center justify-center">
              {accept.includes("image") ? (
                <Image className="w-6 h-6 text-[#00AEEF]" />
              ) : (
                <Upload className="w-6 h-6 text-[#00AEEF]" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-[#3b3b3b]">
                Drop your file here or <span className="text-[#00AEEF]">click to browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Max {maxSizeMB}MB &middot; PNG, JPEG, WebP, PDF
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-500 mt-2" data-testid={`diagram-error-${index}`}>
          {error}
        </p>
      )}
    </div>
  );
}
